import React from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import styled from "styled-components";

import { StandardsAttribution } from "../common/api";
import { ApiContext } from "../common/context/ApiContext";
import {
  AppAnchor,
  BackNavRow,
  LoadingIndicator,
  PageContainer,
  PrimaryButton,
} from "../common/CommonStyles";
import { COLOR_SURFACE_CONTAINER, COLOR_TEXT_SECONDARY } from "../common/colors";

// CC BY 4.0 attributions for the academic-standards data (Learning Commons
// Knowledge Graph). Each group's attribution_statement is the publisher's
// exact credit text and is rendered VERBATIM — reproducing it is the license
// compliance; we never paraphrase or hand-write credit lines. The page is
// deliberately ungated (legal text); only the links leading here are gated
// to the standards pilot.

const Intro = styled.p`
  margin: 16px 0;
`;

const AttributionCard = styled.div`
  background: ${COLOR_SURFACE_CONTAINER};
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
`;

const Statement = styled.div`
  font-size: 14px;
`;

const Meta = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: ${COLOR_TEXT_SECONDARY};
`;

const CenteredState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 0;
`;

const StandardsAttributionsPage: React.FC = () => {
  const { t } = useTranslation();
  const { getStandardsAttributions } = React.useContext(ApiContext);

  const [attributions, setAttributions] = React.useState<
    StandardsAttribution[] | null
  >(null);
  const [error, setError] = React.useState(false);

  const fetchAttributions = React.useCallback(() => {
    setError(false);
    setAttributions(null);
    getStandardsAttributions()
      .then((response) => setAttributions(response.attributions))
      .catch(() => setError(true));
  }, [getStandardsAttributions]);

  React.useEffect(() => {
    fetchAttributions();
  }, [fetchAttributions]);

  return (
    <PageContainer>
      <BackNavRow label={t("generic_nav_back")}>
        <h2>{t("standards_attributions_title")}</h2>
      </BackNavRow>
      <Row className="justify-content-center mt-2">
        <Col xs={12} lg={10} xl={8}>
          {error ? (
            <CenteredState>
              <span>{t("standards_attributions_error")}</span>
              <PrimaryButton onClick={fetchAttributions}>
                {t("standards_modal_retry")}
              </PrimaryButton>
            </CenteredState>
          ) : attributions === null ? (
            <CenteredState>
              <LoadingIndicator size="2x" />
            </CenteredState>
          ) : (
            <>
              <Intro>{t("standards_attributions_intro")}</Intro>
              {attributions.map((attribution) => (
                <AttributionCard key={attribution.attribution_statement}>
                  <Statement>{attribution.attribution_statement}</Statement>
                  <Meta>
                    {[
                      attribution.jurisdictions.join(", "),
                      attribution.subjects.join(", "),
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </Meta>
                  {attribution.license && (
                    <Meta>
                      <AppAnchor
                        href={attribution.license}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {attribution.license}
                      </AppAnchor>
                    </Meta>
                  )}
                </AttributionCard>
              ))}
            </>
          )}
        </Col>
      </Row>
    </PageContainer>
  );
};

export default StandardsAttributionsPage;
